import React, { createContext, useContext, useMemo, useState } from "react";
import translations, { DEFAULT_LANGUAGE } from "./translations";

const STORAGE_KEY = "raiz-language";

const LanguageContext = createContext(null);

function getInitialLanguage() {
  if (typeof window === "undefined") return DEFAULT_LANGUAGE;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored && translations[stored] ? stored : DEFAULT_LANGUAGE;
}

// Looks up `path` (e.g. "hero.cta") in `dict`, falling back to the English
// dictionary (and finally the key itself) whenever a translation is missing.
function lookup(dict, path) {
  const value = path
    .split(".")
    .reduce((acc, key) => (acc == null ? undefined : acc[key]), dict);
  return typeof value === "string" ? value : undefined;
}

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(getInitialLanguage);

  const value = useMemo(() => {
    const changeLanguage = (code) => {
      if (!translations[code]) return;
      setLanguage(code);
      if (typeof window !== "undefined") {
        window.localStorage.setItem(STORAGE_KEY, code);
      }
    };

    const t = (path) =>
      lookup(translations[language], path) ??
      lookup(translations[DEFAULT_LANGUAGE], path) ??
      path;

    return { language, changeLanguage, t };
  }, [language]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return ctx;
}
